import { defineStore } from 'pinia';

export const useCategoryStore = defineStore('category', () => {
    // Nuxt-specifieke API die gebruikt wordt om gegevens op te halen.
    const { $api } = useNuxtApp();

    // Reactieve state om categorieën op te slaan.
    const _categories = ref([]);

    /*
     * Computed properties voor het ophalen van categorieën.
     * Deze eigenschappen zijn efficiënt omdat ze worden bijgewerkt wanneer `_categories` verandert.
     */

    // Haal alle categorieën op.
    const allCategories = computed(() => _categories.value);

    // Haal alleen de bovenliggende categorieën op (categorieën zonder parent_id).
    const topCategories = computed(() => _categories.value.filter(c => c.parent_id == null));

    // Zoek een specifieke categorie op basis van de slug.
    const categoryBySlug = (slug) => _categories.value.find(c => c.slug.nl == slug) || null;

    // Zoek een specifieke categorie op basis van het ID.
    const categoryById = (id) => _categories.value.find(c => c.id == id) || null;

    // Haal een lijst van categorieën op op basis van een lijst met IDs.
    const categoriesByIds = (ids) => _categories.value.filter(c => ids.includes(c.id));

    // Haal alle directe kinderen van een categorie op basis van de `parent_id`.
    const categoriesByParentId = (parent_id) => _categories.value.filter(c => c.parent_id == parent_id);

    /*
     * Functie om alle kinderen van een gegeven categorie, inclusief subcategorieën, op te halen.
     * Dit gebruikt een recursieve aanpak om diep geneste structuren door te lopen.
     */
    const allChildCategories = (parent) => {
        const recurseChildren = (cat, children) => {
            // Voeg de huidige categorie toe aan de lijst.
            children.push(cat);

            // Zoek kinderen van de huidige categorie en doorloop deze recursief.
            for (let child of categoriesByParentId(cat.id))
                children = recurseChildren(child, children);

            return children; // Retourneer de lijst met verzamelde categorieën.
        };

        return recurseChildren(parent, []); // Start met een lege lijst.
    };

    /*
     * Functie om alle categorieën van de server op te halen.
     * Met een `force`-parameter kan het ophalen geforceerd worden, zelfs als er al categorieën in de state zijn.
     */
    const fetchAll = async(force = false) => {
        // Controleer of de categorieën al zijn geladen en forceer ophalen indien nodig.
        if (_categories.value.length > 0 && !force)
            return;

        // API-aanroep om categorieën op te halen.
        return new Promise(async (resolve, reject) => {
            console.log('[PINIA] fetching categories');
            
            // Voer de API-aanroep uit.
            const result = await $api('/category')
                .catch((error) => {
                    reject(error); // Verwerp de belofte bij een fout.
                    return;
                });

            // Controleer of er gegevens in de respons zitten.
            if (!result?.data) {
                reject('No data'); // Verwerp als er geen data is.
                return;
            }

            // Voeg de opgehaalde categorieën toe aan de state.
            addCategoriesToStore(result.data);
            resolve(_categories.value); // Los de belofte op met de bijgewerkte categorieën.
        });
    };

    /*
     * Functie om nieuwe categorieën toe te voegen aan de bestaande state.
     * Voorkomt duplicatie door categorieën met dezelfde ID te overschrijven.
     */
    function addCategoriesToStore(categories) {
        if (categories === null || categories === undefined)
            return;

        // Zorg dat `categories` een array is.
        if (!Array.isArray(categories))
            categories = [categories];

        // Filter bestaande categorieën uit die overlappen met de nieuwe, en voeg de nieuwe toe.
        _categories.value = [
            ..._categories.value.filter(c => !categories.some(n => c.id == n.id)),
            ...categories
        ];
    }

    // Exporteer de functies en computed properties van de store.
    return {
        _categories,             // De ruwe categoriegegevens.
        allCategories,           // Alle categorieën.
        topCategories,           // Alleen bovenliggende categorieën.
        addCategoriesToStore,    // Functie om nieuwe categorieën toe te voegen.
        categoryBySlug,          // Zoeken naar een categorie op basis van slug.
        categoryById,            // Zoeken naar een categorie op basis van ID.
        categoriesByIds,         // Meerdere categorieën ophalen op basis van ID's.
        categoriesByParentId,    // Kindcategorieën ophalen.
        allChildCategories,      // Alle subcategorieën ophalen (recursief).
        fetchAll,                // Ophalen van categorieën vanaf de server.
    };
});

// Hot Module Replacement (HMR) configuratie voor ontwikkelgemak.
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCategoryStore, import.meta.hot));
}
